import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';

export interface LogoBadgeOverlayProps {
    logo: { src: string; background?: string | null; alt?: string };
    /** Absolute-position classes for placement within a `relative` container. */
    className?: string;
    /** Size classes for the badge (default ~8.4rem / size-16 on mobile). */
    sizeClassName?: string;
    /** When set, the badge links to the professional profile (and becomes clickable). */
    to?: string;
}

/**
 * Professional logo badge overlaid on a media element (e.g. the gallery's
 * bottom-right corner). Uses the standard `ProfessionalTileLogo` avatar; the
 * caller positions it with `className` inside a `relative` container.
 *
 * The container's `overflow` must not clip the badge's drop shadow — use
 * `overflow-x-clip` (not `overflow-hidden`) on the media section so the vertical
 * shadow can extend past the edge.
 */
export const LogoBadgeOverlay = ({
    logo,
    className,
    sizeClassName = 'size-16 md:size-[8.4rem]',
    to,
}: LogoBadgeOverlayProps) => {
    const badge = (
        <ProfessionalTileLogo
            image={{ src: logo.src, alt: logo.alt ?? 'Logo' }}
            background={logo.background ?? null}
            className={cn(
                sizeClassName,
                'shadow-[0_8px_24px_rgba(0,0,0,0.18)]',
            )}
        />
    );

    return (
        <div
            className={cn(
                'absolute z-10',
                // Default badges are decorative; a linked badge must catch clicks.
                to ? 'pointer-events-auto' : 'pointer-events-none',
                className,
            )}
        >
            {to ? (
                <RoccoLink to={to} className="block">
                    {badge}
                </RoccoLink>
            ) : (
                badge
            )}
        </div>
    );
};
